import * as _ from 'lodash';

import { Injectable } from '@angular/core';
import { IsActiveMatchOptions, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, merge, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, startWith, switchMap, withLatestFrom } from 'rxjs/operators';

import {
    AuthenticatedUserService,
    LanguageService,
    OrganizationType,
    SegmentService,
    UserService,
    WindowService
} from 'services';

import { AuthService } from '@auth0/auth0-angular';
import { User } from 'app/shared/services/authenticatedUser/types';
import { brandSettingsRequiredPrivileges } from 'app/shared/services/brands.service';
import { TranslationMap } from 'app/shared/services/language.service';
import { FcpIconSVGColors, FcpIconSVGSizes, IconNameMap, MenuListItem } from 'fc-ui-components';
import {
    MainNavigationSummaryBarService,
    SummaryBarConfig
} from 'src/app/components/main-navigation/main-navigation-summary-bar/main-navigation-summary-bar.service';
import { brandShopUrlPrefix, retailersUrlPrefix } from 'src/app/retailer-platform/brand-shop/utils';
import { PlatformToolbarAction, PlatformToolbarLink } from './types';

@Injectable({
    providedIn: 'root'
})
export class MainNavigationService {
    public toolbarLinks: Observable<PlatformToolbarLink[]>;
    public toolbarAction: Observable<PlatformToolbarAction>;
    public toolbarStoresAction: Observable<PlatformToolbarAction>;
    public isToolbarVisible: Observable<boolean>;
    public isToolbarVisibleForDesktop: Observable<boolean>;
    public activePageTitle: Observable<string>;
    public settingsList: Observable<Array<PlatformToolbarLink | MenuListItem>>;
    public isDownloadListOpen: Observable<boolean>;
    private toolbarLinksSubject: BehaviorSubject<PlatformToolbarLink[]>;
    private toolbarActionSubject: BehaviorSubject<PlatformToolbarAction>;
    private toolbarStoresActionSubject: BehaviorSubject<PlatformToolbarAction>;
    private activePageTitleSubject: BehaviorSubject<string>;
    private isToolbarHidden = new BehaviorSubject<boolean>(false);
    private isDownloadListOpenSubject = new BehaviorSubject<boolean>(false);

    constructor(
        private router: Router,
        private userService: UserService,
        private languageService: LanguageService,
        private authenticatedUserService: AuthenticatedUserService,
        private windowService: WindowService,
        private segmentService: SegmentService,
        private mainNavigationSummaryBarService: MainNavigationSummaryBarService,
        private auth0Service: AuthService
    ) {
        this.toolbarLinksSubject = new BehaviorSubject<PlatformToolbarLink[]>([]);
        this.toolbarLinks = this.toolbarLinksSubject.asObservable().pipe(shareReplay(1));
        this.toolbarActionSubject = new BehaviorSubject<PlatformToolbarAction>(null);
        this.toolbarAction = this.toolbarActionSubject.asObservable().pipe(shareReplay(1));
        this.toolbarStoresActionSubject = new BehaviorSubject<PlatformToolbarAction>(null);
        this.toolbarStoresAction = this.toolbarStoresActionSubject.asObservable().pipe(shareReplay(1));
        this.activePageTitleSubject = new BehaviorSubject<string>(null);
        this.activePageTitle = combineLatest([
            this.activePageTitleSubject.asObservable(),
            languageService.onLangChange().pipe(startWith(null))
        ]).pipe(
            switchMap(([titleKey]) => (titleKey ? languageService.translateObservable(titleKey) : of(''))),
            shareReplay(1)
        );
        this.isToolbarVisible = combineLatest([this.toolbarLinks, this.toolbarAction]).pipe(
            map(([links, actions]) => !!(!_.isEmpty(actions) || links?.length)),
            shareReplay(1)
        );

        this.isToolbarVisibleForDesktop = combineLatest([
            this.isToolbarVisible,
            this.isToolbarHidden.asObservable()
        ]).pipe(
            map(([isVisible, isHidden]) => {
                const matchingOptions = {
                    queryParams: 'ignored',
                    matrixParams: 'ignored',
                    paths: 'subset',
                    fragment: 'ignored'
                } as IsActiveMatchOptions;

                const isBrandShopRoute = this.router.isActive(
                    `${retailersUrlPrefix}/${brandShopUrlPrefix}`,
                    matchingOptions
                );

                const isVisibleForDesktop = !isBrandShopRoute && isVisible;
                return isVisibleForDesktop && !isHidden;
            })
        );

        const isUserBeingImpersonated$ = this.authenticatedUserService.isUserImpersonatedByAdmin$.pipe(
            catchError(() => of(false)),
            shareReplay(1)
        );

        this.settingsList = merge(
            languageService.onLangChange(),
            authenticatedUserService.user,
            isUserBeingImpersonated$
        ).pipe(
            startWith(null),
            switchMap(() =>
                languageService
                    .translateObservable([
                        'NAV_BAR.MANAGE_STORES',
                        'CLARA_PAGE.USERS.NAV_TITLE',
                        'NAV_BAR.TEAM',
                        'NAV_BAR.ACCOUNT',
                        'BRAND_PROFILES.TITLE',
                        'NOTIFICATIONS',
                        'FAQ_LINK.TITLE',
                        'NAV_HEADER_LOGOUT',
                        'DOWNLOAD_SETTINGS.TITLE',
                        'NAV_HEADER_STOP_VIEWING_AS'
                    ])
                    .pipe(
                        withLatestFrom(authenticatedUserService.user, isUserBeingImpersonated$),
                        map(([translatedValues, user, isUserBeingImpersonated]) => [
                            ...(userService.isRetailer()
                                ? this.getSettingItemsForRetailer({
                                      translatedValues,
                                      user,
                                      isUserBeingImpersonated
                                  })
                                : userService.isManufacturer()
                                  ? this.getSettingItemsForManufacturer({
                                        translatedValues,
                                        user,
                                        isUserBeingImpersonated
                                    })
                                  : []),
                            ...(user.assignedOrganizations?.length > 1
                                ? [
                                      {
                                          text: _.truncate(user.organization.name, { length: 25 }),
                                          subtext: _.truncate(user.email, { length: 40 }),
                                          triggerHandler: () =>
                                              this.auth0Service.loginWithRedirect({
                                                  appState: {
                                                      switchOrgs: true
                                                  }
                                              }),
                                          separator: true,
                                          icon: {
                                              name: IconNameMap.Switch,
                                              position: 'right',
                                              color: FcpIconSVGColors.NeutralDark,
                                              size: FcpIconSVGSizes.Large
                                          }
                                      }
                                  ]
                                : []),
                            ...(isUserBeingImpersonated
                                ? [
                                      {
                                          text: translatedValues.NAV_HEADER_STOP_VIEWING_AS,
                                          subtext: _.truncate(user.email, { length: 40 }),
                                          triggerHandler: () => this.authenticatedUserService.stopImpersonatingUser(),
                                          separator: true,
                                          icon: {
                                              name: IconNameMap.User,
                                              position: 'right',
                                              color: FcpIconSVGColors.NeutralDark,
                                              size: FcpIconSVGSizes.Large
                                          }
                                      }
                                  ]
                                : [])
                        ]),
                        catchError((error) => [])
                    )
            ),
            shareReplay(1)
        );
        this.isDownloadListOpen = this.isDownloadListOpenSubject.asObservable().pipe(shareReplay(1));
    }

    setToolbarLinks(toolbarLinks: PlatformToolbarLink[]) {
        this.toolbarLinksSubject.next(toolbarLinks);
    }

    clearToolbarLinks() {
        this.toolbarLinksSubject.next([]);
    }

    setToolbarAction(toolbarAction: PlatformToolbarAction) {
        this.toolbarActionSubject.next(toolbarAction);
    }

    setToolbarStoresAction(toolbarAction: PlatformToolbarAction) {
        this.toolbarStoresActionSubject.next(toolbarAction);
    }

    setPageTitle(titleKey: string) {
        this.activePageTitleSubject.next(titleKey);
    }

    removeToolbarAction() {
        this.toolbarActionSubject.next(null);
    }

    removeToolbarStoresAction() {
        this.toolbarStoresActionSubject.next(null);
    }

    resetToolbar() {
        this.toolbarLinksSubject.next([]);
        this.toolbarActionSubject.next(null);
        this.toolbarStoresActionSubject.next(null);
    }

    hideToolbar() {
        this.isToolbarHidden.next(true);
    }

    restoreToolbar() {
        this.isToolbarHidden.next(false);
    }

    sendNavigationMenuItemTrackEvent({
        eventName,
        eventPayload
    }: {
        eventName: string;
        eventPayload: { target: string };
    }) {
        this.segmentService.track(eventName, eventPayload);
    }

    resetSummaryBar() {
        this.mainNavigationSummaryBarService.resetSummaryBar();
    }

    setSummaryBarConfig(config: SummaryBarConfig) {
        this.mainNavigationSummaryBarService.setSummaryBar(config);
    }

    setIsDownloadListOpen(isOpen: boolean) {
        this.isDownloadListOpenSubject.next(isOpen);
        if (isOpen) {
            this.segmentService.track('[CO] View Download list', {
                contentContext: 'Primary navigation'
            });
        }
    }

    private getSettingItemsForRetailer({
        translatedValues,
        user,
        isUserBeingImpersonated
    }: {
        translatedValues: TranslationMap;
        user: User;
        isUserBeingImpersonated: boolean;
    }): Array<PlatformToolbarLink | MenuListItem> {
        return [
            {
                label: translatedValues['NAV_BAR.ACCOUNT'],
                route: { target: `/${retailersUrlPrefix}/settings/account` }
            },
            {
                label: translatedValues['NAV_BAR.TEAM'],
                route: { target: `/${retailersUrlPrefix}/settings/team` }
            },
            {
                label: translatedValues['DOWNLOAD_SETTINGS.TITLE'],
                route: { target: `/${retailersUrlPrefix}/settings/download-settings` }
            },
            {
                label: translatedValues['NAV_BAR.MANAGE_STORES'],
                route: { target: `/${retailersUrlPrefix}/settings/stores` }
            },
            {
                label: translatedValues['CLARA_PAGE.USERS.NAV_TITLE'],
                route: { target: `/${retailersUrlPrefix}/settings/clara` }
            },
            {
                text: translatedValues['FAQ_LINK.TITLE'],
                triggerHandler: () =>
                    this.windowService.open(`https://help.fashion.cloud/${this.languageService.getFAQLanguage()}/`)
            },
            {
                text: translatedValues.NAV_HEADER_LOGOUT,
                triggerHandler: () => this.authenticatedUserService.logout()
            }
        ] as PlatformToolbarLink[] | MenuListItem[];
    }

    private getSettingItemsForManufacturer({
        translatedValues,
        user,
        isUserBeingImpersonated
    }: {
        translatedValues: TranslationMap;
        user: User;
        isUserBeingImpersonated: boolean;
    }): Array<PlatformToolbarLink | MenuListItem> {
        return [
            { label: translatedValues['NAV_BAR.TEAM'], route: { target: '/settings/team' } },
            {
                label: translatedValues['NAV_BAR.ACCOUNT'],
                route: { target: '/settings/account' }
            },
            ...(this.userService.hasAtLeastOnePrivilege(brandSettingsRequiredPrivileges[OrganizationType.Manufacturer])
                ? [
                      {
                          label: translatedValues['BRAND_PROFILES.TITLE'],
                          route: { target: '/settings/brands' }
                      }
                  ]
                : []),
            {
                label: translatedValues.NOTIFICATIONS,
                route: { target: 'settings/notifications' }
            },
            {
                label: translatedValues['DOWNLOAD_SETTINGS.TITLE'],
                route: { target: 'settings/download-settings' }
            },
            {
                text: translatedValues['FAQ_LINK.TITLE'],
                triggerHandler: () =>
                    this.windowService.open(`https://help.fashion.cloud/${this.languageService.getFAQLanguage()}/`)
            },
            {
                text: translatedValues.NAV_HEADER_LOGOUT,
                triggerHandler: () => this.authenticatedUserService.logout()
            }
        ] as PlatformToolbarLink[] | MenuListItem[];
    }
}
